import api from './api';

export interface Assessment {
  id: number;
  title: string;
  description?: string;
  courseId: number;
  createdBy: number;
  creatorRole: string;
  scheduledDate: string;
  dueDate?: string;
  durationMinutes?: number;
  passingScore: number;
  maxAttempts: number;
  requiresWorkSubmission: boolean;
  allowReassessment: boolean;
  status: string;
  topicIds: number[];
  questionCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssessmentAttempt {
  id: number;
  assessmentId: number;
  assessmentTitle?: string;
  studentId: number;
  topicIds: number[];
  startedAt: string;
  submittedAt?: string;
  completedAt?: string;
  score?: number; // Percentage (0-100)
  pointsEarned?: number;
  pointsTotal?: number;
  correctAnswers?: number;
  totalQuestions?: number;
  timeTakenMinutes?: number;
  status: string;
  passed?: boolean;
  workSubmitted?: boolean;
  answers?: Record<string, any>;
  workFileUrls?: string[];
  gradingNotes?: string;
  reassessmentAllowed?: boolean;
  reassessmentGatedUntil?: string;
}

export interface AssessmentSubmission {
  answers: Record<string, any>;
  workFileUrls?: string[];
}

class AssessmentService {
  /**
   * Get available assessments for a course
   */
  async getAvailableAssessments(courseId: number): Promise<Assessment[]> {
    const response = await api.get<Assessment[]>(`/api/student/assessments/course/${courseId}`);
    return response.data;
  }

  /**
   * Start an assessment attempt
   */
  async startAssessment(assessmentId: number): Promise<AssessmentAttempt> {
    const response = await api.post<AssessmentAttempt>(
      `/api/student/assessments/${assessmentId}/start`
    );
    return response.data;
  }

  /**
   * Submit an assessment attempt
   */
  async submitAssessment(
    attemptId: number,
    submission: AssessmentSubmission
  ): Promise<AssessmentAttempt> {
    const response = await api.post<AssessmentAttempt>(
      `/api/student/assessments/attempts/${attemptId}/submit`,
      submission
    );
    return response.data;
  }

  /**
   * Get assessment attempts for a student
   */
  async getAssessmentAttempts(assessmentId: number): Promise<AssessmentAttempt[]> {
    const response = await api.get<AssessmentAttempt[]>(
      `/api/student/assessments/${assessmentId}/attempts`
    );
    return response.data;
  }
}

export default new AssessmentService();
